/**
 * v3.15-1123 — Let's Get This Deal modal.
 *
 * Internal team rally tool. Lets a user select role-based and/or
 * arbitrary Hub users as recipients, write an optional rally message,
 * preview the deal context, and fire an internal email.
 *
 * The client is NEVER copied. No tasks/reminders are created. Deals
 * can be rallied unlimited times — each send appends a new activity.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, Megaphone, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ROLE_OPTIONS = [
  // v3.15-1128: legacy deals frequently have only `_name` fields populated
  // (no `_id`). The checkbox should enable as long as ANY of the listed
  // fields is set — backend resolves `_id` first, then falls back to a
  // name-based user lookup.
  { key: 'assigned_rep',        label: 'Assigned Rep',        clientFields: ['sales_rep_id', 'sales_rep_name', 'source_rep_name'] },
  { key: 'director',            label: 'Director',            clientFields: ['director_id', 'director_name'] },
  { key: 'company_manager',     label: 'Company Manager',     clientFields: ['manager_director_id', 'manager_director_name', 'manager_name'] },
  { key: 'concierge',           label: 'Concierge',           clientFields: ['application_concierge_id', 'application_concierge_name', 'ptc_rep_name'] },
  { key: 'application_manager', label: 'Application Manager', clientFields: ['eqf_manager_id', 'eqf_manager_name'] },
  { key: 'vo_officer',          label: 'VO Officer',          clientFields: ['vo_officer_id', 'vo_officer_name'] },
];

export default function LetsGetThisDealModal({
  open,
  onOpenChange,
  client,
  companyName,
  onSuccess,
}) {
  const [message, setMessage] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]); // [{id, full_name, email}]
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);

  // Reset on open
  useEffect(() => {
    if (open) {
      setMessage('');
      setSelectedRoles([]);
      setSelectedUsers([]);
      setUserQuery('');
      setUserResults([]);
    }
  }, [open]);

  // Live user search (debounced)
  useEffect(() => {
    if (!open) return;
    const q = (userQuery || '').trim();
    if (q.length < 2) {
      setUserResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get('/users');
        const lc = q.toLowerCase();
        const list = (Array.isArray(data) ? data : []).filter(u => {
          const name = (u.full_name || '').toLowerCase();
          const email = (u.email || '').toLowerCase();
          return name.includes(lc) || email.includes(lc);
        }).slice(0, 12);
        setUserResults(list);
      } catch {
        setUserResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [userQuery, open]);

  const toggleRole = (key) => {
    setSelectedRoles(prev => prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]);
  };

  const addUser = (u) => {
    if (!u || !u.id) return;
    if (selectedUsers.some(s => s.id === u.id)) return;
    setSelectedUsers(prev => [...prev, { id: u.id, full_name: u.full_name, email: u.email }]);
    setUserQuery('');
    setUserResults([]);
  };

  const removeUser = (id) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== id));
  };

  const currentStage = client?.deal_stage || '—';
  const lastCompletedStage = client?.last_completed_stage || '—';

  const totalRecipients = useMemo(() => {
    // Count of unique role-filled users + manually-added users.
    // v3.15-1128: role count = number of selected roles that have ANY
    // assignment (id or name) on the client doc — the backend resolves
    // ids first then falls back to name-based lookup.
    const roleKeys = new Set();
    selectedRoles.forEach(rk => {
      const opt = ROLE_OPTIONS.find(o => o.key === rk);
      const has = (opt?.clientFields || []).some(f => !!client?.[f]);
      if (has) roleKeys.add(rk);
    });
    const userIds = new Set(selectedUsers.map(u => u.id));
    // Roles + manual users may overlap if the role-resolved user matches
    // a manually-added one — but we can't easily dedupe here without an
    // API call, so we just report the sum (slight over-count is fine
    // for the counter — backend dedupes for real).
    return roleKeys.size + userIds.size;
  }, [selectedRoles, selectedUsers, client]);

  const canSend = totalRecipients > 0 && !sending;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const payload = {
        message: message || '',
        roles: selectedRoles,
        user_ids: selectedUsers.map(u => u.id),
      };
      const { data } = await api.post(`/clients/${client?.id}/lets-get-this-deal`, payload);
      toast.success(`Rallied ${data?.recipients?.length || 0} teammate(s)`);
      onOpenChange(false);
      if (typeof onSuccess === 'function') onSuccess(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to send rally';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="lets-get-this-deal-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-semibold">
            <Megaphone className="h-4 w-4 text-cyan-600" />
            Let&apos;s Get This Deal — Internal Rally
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Send an internal rally email to selected teammates. The client is{' '}
            <span className="font-semibold">not</span> copied.
          </DialogDescription>
        </DialogHeader>

        {/* Preview block */}
        <div className="rounded border bg-slate-50 px-3 py-2 text-[11px] space-y-0.5" data-testid="rally-preview">
          <div><span className="text-slate-500">Client:</span> <span className="font-semibold text-slate-800">{client?.full_name || client?.business_name || '—'}</span></div>
          <div><span className="text-slate-500">Company:</span> <span className="text-slate-700">{companyName || '—'}</span></div>
          <div><span className="text-slate-500">Current Stage:</span> <span className="font-semibold text-slate-800">{currentStage}</span></div>
          <div><span className="text-slate-500">Last Completed Stage:</span> <span className="text-slate-700">{lastCompletedStage}</span></div>
        </div>

        {/* Recipient roles */}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-700">Recipients (roles)</Label>
          <div className="grid grid-cols-2 gap-1.5">
            {ROLE_OPTIONS.map(opt => {
              const assignedValue = (opt.clientFields || []).map(f => client?.[f]).find(v => !!v) || null;
              const disabled = !assignedValue;
              const checked = selectedRoles.includes(opt.key);
              // If the value looks like a UUID, hide it (uglier); show name if it's a name
              const isUuid = typeof assignedValue === 'string' && /^[0-9a-f-]{30,}$/i.test(assignedValue);
              const previewName = (!disabled && !isUuid) ? assignedValue : null;
              return (
                <div
                  key={opt.key}
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  onClick={() => !disabled && toggleRole(opt.key)}
                  onKeyDown={(e) => {
                    if (disabled) return;
                    if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleRole(opt.key); }
                  }}
                  className={`flex items-center gap-2 rounded border px-2 py-1.5 text-xs select-none ${
                    disabled
                      ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                      : checked
                        ? 'border-cyan-400 bg-cyan-50 text-cyan-900 cursor-pointer'
                        : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50 cursor-pointer'
                  }`}
                  data-testid={`rally-role-${opt.key}`}
                >
                  <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={() => !disabled && toggleRole(opt.key)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`rally-role-${opt.key}-checkbox`}
                  />
                  <span className="flex-1 truncate">
                    {opt.label}
                    {previewName && <span className="ml-1 text-[10px] text-slate-500">— {previewName}</span>}
                  </span>
                  {disabled && <span className="text-[9px] text-slate-400 italic ml-auto">unassigned</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Arbitrary user search */}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-700">Add specific user(s)</Label>
          <div className="relative">
            <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            <Input
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              placeholder="Search by name or email…"
              className="pl-7 h-8 text-xs"
              data-testid="rally-user-search-input"
            />
            {searching && (
              <Loader2 className="absolute right-2 top-2 h-3.5 w-3.5 text-slate-400 animate-spin" />
            )}
            {userResults.length > 0 && (
              <div
                className="absolute z-10 mt-1 w-full max-h-44 overflow-auto rounded border bg-white shadow-lg"
                data-testid="rally-user-search-results"
              >
                {userResults.map(u => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => addUser(u)}
                    className="block w-full px-2 py-1.5 text-left text-xs hover:bg-slate-50 border-b last:border-b-0"
                    data-testid={`rally-user-result-${u.id}`}
                  >
                    <div className="font-medium text-slate-800">{u.full_name || u.email}</div>
                    <div className="text-[10px] text-slate-500">{u.email}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedUsers.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1" data-testid="rally-selected-users">
              {selectedUsers.map(u => (
                <span
                  key={u.id}
                  className="inline-flex items-center gap-1.5 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-800 px-2 py-0.5 text-[11px]"
                  data-testid={`rally-selected-user-${u.id}`}
                >
                  {u.full_name || u.email}
                  <button
                    type="button"
                    onClick={() => removeUser(u.id)}
                    className="hover:text-red-600"
                    aria-label="Remove"
                    data-testid={`rally-remove-user-${u.id}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Optional message */}
        <div className="space-y-1.5">
          <Label htmlFor="rally-message" className="text-xs font-semibold text-slate-700">
            Why are we rallying? <span className="font-normal text-slate-400">(optional)</span>
          </Label>
          <Textarea
            id="rally-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="e.g. Client is highly motivated but lender response has stalled. Looking for additional funding options."
            className="text-xs min-h-[64px]"
            data-testid="rally-message-input"
          />
        </div>

        <DialogFooter className="gap-2">
          <div className="mr-auto text-[11px] text-slate-500" data-testid="rally-recipient-count">
            {totalRecipients} recipient{totalRecipients === 1 ? '' : 's'} selected
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            data-testid="rally-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={send}
            disabled={!canSend}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="rally-send-btn"
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Megaphone className="h-3.5 w-3.5 mr-1" />}
            Send Rally
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
